import { PrimaryCTA } from "@/components/CTA";
import { BoxedNote, Checklist } from "@/components/Notebook";
import { ROUTES } from "@/config/links";
import { useLang } from "@/i18n";
import { useSectionTracking } from "@/hooks/useSectionTracking";

/**
 * Inline "take the free diagnostic" promo card, sized to sit inside an
 * article's narrow content column (unlike the full-bleed
 * ReadinessCheckBanner section). Shown on specific articles via
 * ArticleDetail's DIAGNOSTIC_CTA_SLUGS allow-list — not every article,
 * so it stays a deliberate, relevant recommendation rather than a
 * sitewide pop-up.
 *
 * Tracking (to compare this card's engagement against the homepage's
 * own diagnostic CTAs in PostHog): the whole card fires `section_viewed`
 * /`section_time_spent` under a per-article id (`articleSlug` below) —
 * "did a reader actually see this card" — while the button itself
 * already fires `cta_viewed` (scroll-into-view) and `cta_click` via
 * PrimaryCTA, both tagged `cta_location: "article_inline"` and the
 * current `page_path`, so every event here is already filterable by
 * page/article vs. any homepage CTA with no further setup.
 */
export const ArticleDiagnosticCTA = ({ articleSlug }) => {
  const { t } = useLang();
  const sectionRef = useSectionTracking(`article_diagnostic_cta__${articleSlug}`);
  return (
    <div className="my-12 md:my-16" ref={sectionRef} data-testid="article-diagnostic-cta">
      <BoxedNote className="w-full">
        <p className="font-body text-xs uppercase tracking-[0.18em] text-terracotta mb-3">
          {t("article_cta.diagnostic_badge")}
        </p>
        <h3 className="font-serif text-2xl md:text-3xl text-ink leading-tight mb-5 max-w-md">
          {t("article_cta.diagnostic_title")}
        </h3>
        <Checklist
          className="mb-7"
          items={[
            t("article_cta.diagnostic_bullet_score"),
            t("article_cta.diagnostic_bullet_report"),
            t("article_cta.diagnostic_bullet_exercise"),
            t("article_cta.diagnostic_bullet_email"),
          ]}
        />
        <PrimaryCTA
          href={ROUTES.nextPresentationReadinessCheck}
          testId="article-diagnostic-cta-button"
          location="article_inline"
          targetAssetId="next_presentation_readiness"
          articleSlug={articleSlug}
        >
          {t("article_cta.diagnostic_cta")}
        </PrimaryCTA>
      </BoxedNote>
    </div>
  );
};
